import AppRoutes from './AppRoutes';
import { useEffect, useState } from 'react';
import ScrollToTop from './utils/ScrollToTop';
import { BrowserRouter, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import { restoreSession } from './features/auth/authSlice';
import { HelmetProvider } from 'react-helmet-async';

import { identify, setUserProperties } from './lib/mixpanel';
import { AddToCartModalProvider } from './utils/productCard/AddToCartModalContext';
import PersonalShopperButton from './ui/components/PersonalShopperButton';

const MonoCallbackHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const orderId = searchParams.get('orderID');
    const status = searchParams.get('status');
    const reason = searchParams.get('reason');

    if (orderId && status === 'success' && reason === 'mandate_success') {
      navigate(`/cart-items/checkout/direct-debit/success?orderid=${orderId}`, {
        replace: true,
      });
    }
  }, [searchParams, navigate]);

  return null;
};

const App = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [toastShown, setToastShown] = useState(false);
  const { isAuthenticated, error } = useSelector((state) => state.auth);

  const user = useSelector((state) => state.user?.data);

  // The DevRev chat SDK is loaded and initialised from index.html after page load
  const dispatch = useDispatch();

  useEffect(() => {
    const initSession = async () => {
      try {
        await dispatch(restoreSession()).unwrap();
      } catch (error) {
        // Session restoration failed (no token, expired, or invalid)
        // User will be redirected to login by ProtectedRoute if they try to access protected pages
        if (error === 'Token has expired') {
          toast.info('Your session has expired. Please log in again.', {
            className:
              'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
            duration: 3000,
          });
        }
      }
    };

    initSession();
  }, [dispatch]);

  useEffect(() => {
    const updateOnlineStatus = () => {
      const onlineStatus = navigator.onLine;
      setIsOnline(onlineStatus);

      if (!onlineStatus && !toastShown) {
        toast.error(
          'You are currently offline. Please check your internet connection.',
          {
            className:
              'bg-[var(--yellow-primary)] text-black text-sm px-1 py-1 rounded-md min-h-0',
            position: 'bottom-left',
            duration: 5000,
          }
        );
        setToastShown(true);
      }

      if (onlineStatus && toastShown) {
        toast.success('You are back online.', {
          className:
            'bg-green-500 text-white text-sm px-1 py-1 rounded-md min-h-0',
          position: 'bottom-left',
          duration: 3000,
        });
        setToastShown(false);
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, [toastShown]);

  // Mixpanel identity tracking
  useEffect(() => {
    if (isAuthenticated) {
      if (user) {
        const name = `${user.firstName} ${user.lastName}`;
        // Identify this user for Mixpanel
        identify(user.userID);
        setUserProperties({
          $name: name || 'guest',
          $email: user?.email,
          $phone: user?.phoneNumber,
          joined_at: new Date().toISOString(),
        });
      }
    }
  }, [user, isAuthenticated]);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <MonoCallbackHandler />
        <ScrollToTop />
        <Toaster position="top-right" duration={5000} richColors />
        <PersonalShopperButton />
        <AddToCartModalProvider>
          <AppRoutes />
        </AddToCartModalProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
